import { Injectable } from '@angular/core';
import { ValidationResult } from '../interfaces/event-script.models';

/**
 * Serviço para validação de scripts TypeScript/JavaScript customizados
 * Garante segurança e sintaxe correta dos scripts no modo expert
 */
@Injectable({ providedIn: 'root' })
export class ScriptValidatorService {

  /**
   * Lista de palavras/APIs proibidas por questões de segurança
   */
  private readonly FORBIDDEN_KEYWORDS = [
    'eval',
    'Function',
    'window',
    'document',
    'import',
    'require',
    'process',
    'global',
    '__dirname',
    '__filename',
    'XMLHttpRequest',
    'fetch', // Pode ser habilitado futuramente se necessário
  ];

  /**
   * Valida código TypeScript/JavaScript
   * @param code Código a ser validado
   * @returns Resultado da validação com erros e warnings
   */
  validate(code: string): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!code || code.trim().length === 0) {
      errors.push('O código não pode estar vazio');
      return { valid: false, errors, warnings };
    }

    // Verificar palavras proibidas
    this.FORBIDDEN_KEYWORDS.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
      if (regex.test(code)) {
        errors.push(`Uso de '${keyword}' não permitido por segurança`);
      }
    });

    // Verificar tentativas de acesso a propriedades globais
    if (code.includes('this.') || code.includes('self.')) {
      warnings.push('Evite usar \'this\' ou \'self\' - use a API fornecida (context, api)');
    }

    // Verificar sintaxe básica tentando criar a função
    try {
      // eslint-disable-next-line no-new-func
      new Function('context', 'api', code);
    } catch (e: any) {
      errors.push(`Erro de sintaxe: ${e.message}`);
    }

    // Validar que o código usa as APIs fornecidas
    if (!code.includes('api.') && !code.includes('context.')) {
      warnings.push('O código não parece usar a API fornecida. Use \'api\' para ações e \'context\' para dados.');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Valida referências a componentes/tabs no código
   * @param code Código a ser validado
   * @param availableIds IDs disponíveis no canvas (tabs, panels, fields)
   * @returns Resultado da validação
   */
  validateReferences(code: string, availableIds: string[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Extrair IDs referenciados no código (strings entre aspas)
    const stringMatches = code.match(/['"`]([^'"`]+)['"`]/g);
    
    if (stringMatches) {
      stringMatches.forEach(match => {
        const id = match.replace(/['"`]/g, '');
        
        // Se parece com um ID (formato comum) e não existe no canvas
        if (id.length > 3 && id.includes('-') && !availableIds.includes(id)) {
          warnings.push(`ID '${id}' não encontrado no canvas atual`);
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Estima complexidade do script (previne scripts muito longos)
   * @param code Código a ser analisado
   * @returns true se complexidade é aceitável
   */
  checkComplexity(code: string): { acceptable: boolean; reason?: string } {
    const lines = code.split('\n').length;
    const maxLines = 100;

    if (lines > maxLines) {
      return {
        acceptable: false,
        reason: `Script muito longo (${lines} linhas). Máximo permitido: ${maxLines} linhas.`
      };
    }

    // Verificar loops aninhados (potencial loop infinito)
    const forLoops = (code.match(/for\s*\(/g) || []).length;
    const whileLoops = (code.match(/while\s*\(/g) || []).length;
    
    if (forLoops + whileLoops > 3) {
      return {
        acceptable: false,
        reason: 'Muitos loops detectados. Simplifique a lógica para evitar problemas de performance.'
      };
    }

    return { acceptable: true };
  }
}



